/**
 * ZenGUARD "Find my size": <zg-size-finder>, in two places.
 *   - Drawer: blocks/zg-size-finder.liquid, inside Horizon's <theme-drawer> (assets/theme-drawer.js) on the product
 *     page; open/close go through it.
 *   - Inline: sections/zg-size-guide.liquid on a page (no drawer, no variant picker).
 *
 * snippets/zg-size-finder-body.liquid renders everything the shopper reads: one Depth radio per depth, one Size radio
 * per depth × size variant (hidden and disabled unless its depth is selected) and one <template> per variant holding
 * the result lines. This element only:
 *   - shows the size radios of the selected depth, keeping the size when that depth has it (else the first available);
 *   - copies the selected variant's result template into the polite live region;
 *   - drawer: preselects the page picker's current depth + size every time the drawer opens;
 *   - drawer: "Shop this size" selects both values (and the first pack value, with a pack option: data-pack) on the
 *     page's main variant picker with ONE change event (one fetch, one ProductSelectEvent carrying the real variant
 *     id), closes the drawer, and brings the picker into view when the drawer was opened from further down the page.
 *     Without a usable picker it navigates to the variant URL;
 *   - drawer, theme editor: opens while its block is selected (also across section re-renders);
 *   - inline: "Shop this size" is a submit button of a GET form to the product URL (the checked size radio is sent as
 *     `variant`), so the browser opens the variant's page by itself; this element leaves it alone. On connect and on
 *     pageshow it shows the sizes of whichever depth is checked, because Back can restore an earlier choice into the
 *     server-rendered radios.
 */

const PAGE_PICKER_SELECTOR = 'variant-picker[data-template-product-match="true"]';
const UPGRADE_TIMEOUT_MS = 3000;

/** Block id selected in the theme editor. Module state, so it survives the section re-render after a setting change. */
let editorSelectedBlockId = '';

/**
 * Resolves once `element` has been upgraded and exposes `method` (or after a timeout).
 * @param {Element} element
 * @param {string} method
 * @returns {Promise<boolean>}
 */
async function whenUpgraded(element, method) {
  if (typeof (/** @type {any} */ (element)[method]) === 'function') return true;

  await Promise.race([
    customElements.whenDefined(element.localName),
    new Promise((resolve) => setTimeout(resolve, UPGRADE_TIMEOUT_MS)),
  ]);

  return typeof (/** @type {any} */ (element)[method]) === 'function';
}

/**
 * Option value ids currently selected in a Horizon variant picker (buttons, swatches or dropdowns).
 * @param {Element} picker
 * @returns {Set<string>}
 */
function selectedValueIds(picker) {
  const ids = new Set();

  for (const input of picker.querySelectorAll('fieldset input:checked')) {
    if (input instanceof HTMLElement && input.dataset.optionValueId) ids.add(input.dataset.optionValueId);
  }

  for (const select of picker.querySelectorAll('select')) {
    const option = select.selectedOptions[0];
    if (option?.dataset.optionValueId) ids.add(option.dataset.optionValueId);
  }

  return ids;
}

class ZgSizeFinder extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    // A copy cloned into a quick-add modal or a product card stays inert.
    if (this.closest('quick-add-dialog, quick-add-component, product-card')) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });

    const drawer = this.#drawer;
    if (!drawer) {
      // Inline. After Back the browser may restore an earlier choice into the radios once this has run.
      this.#showCheckedDepth();
      window.addEventListener('pageshow', () => this.#showCheckedDepth(), { signal });
      return;
    }

    drawer.addEventListener('theme-drawer:open', this.#onOpen, { signal });
    drawer.addEventListener('theme-drawer:close', this.#onClose, { signal });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
      document.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });
      if (editorSelectedBlockId && editorSelectedBlockId === this.dataset.blockId) this.#openDrawer();
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @returns {(HTMLElement & { open: Function, close: Function, isOpen: boolean }) | null} */
  get #drawer() {
    return /** @type {any} */ (this.closest('theme-drawer'));
  }

  /** @returns {HTMLInputElement[]} */
  get #depthInputs() {
    return Array.from(this.querySelectorAll('input[data-zg-sf-depth]'));
  }

  /** @returns {HTMLInputElement[]} */
  get #sizeInputs() {
    return Array.from(this.querySelectorAll('input[data-zg-sf-size]'));
  }

  /** @returns {HTMLInputElement | undefined} */
  get #checkedSize() {
    return this.#sizeInputs.find((input) => input.checked && !input.disabled);
  }

  /**
   * The product page's main variant picker for this product, if any (never one in a dialog or product card).
   * @returns {(HTMLElement & { updateSelectedOption?: Function }) | null}
   */
  #pagePicker() {
    for (const picker of document.querySelectorAll(PAGE_PICKER_SELECTOR)) {
      if (!(picker instanceof HTMLElement)) continue;
      if (picker.closest('dialog, product-card, quick-add-component')) continue;
      if (this.dataset.productId && picker.dataset.productId !== this.dataset.productId) continue;
      return picker;
    }
    return null;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const { target } = event;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.hasAttribute('data-zg-sf-depth')) {
      this.#showDepth(target.value);
      this.#renderResult();
    } else if (target.hasAttribute('data-zg-sf-size')) {
      this.#renderResult();
    }
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const shop = event.target instanceof Element ? event.target.closest('[data-zg-sf-shop]') : null;
    if (!shop) return;

    // Inline: a submit button; its form opens the variant's page.
    if (shop instanceof HTMLButtonElement && shop.type === 'submit') return;

    this.#shop();
  };

  /** Horizon re-fires theme-drawer:open when an open drawer is brought to front; keep the shopper's picks then. */
  #isOpen = false;

  #onOpen = () => {
    if (this.#isOpen) return;
    this.#isOpen = true;
    this.#preselectFromPage();
  };

  #onClose = () => {
    this.#isOpen = false;
  };

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    if (/** @type {CustomEvent} */ (event).detail?.blockId !== this.dataset.blockId) return;
    editorSelectedBlockId = this.dataset.blockId ?? '';
    this.#openDrawer();
  };

  /** @param {Event} event */
  #onBlockDeselect = (event) => {
    if (/** @type {CustomEvent} */ (event).detail?.blockId !== this.dataset.blockId) return;
    editorSelectedBlockId = '';
    this.#drawer?.close?.();
  };

  async #openDrawer() {
    const drawer = this.#drawer;
    if (!drawer || !(await whenUpgraded(drawer, 'open'))) return;
    if (!drawer.isOpen) drawer.open();
  }

  /**
   * Shows the size radios of one depth and keeps a sensible size checked.
   * @param {string} depthId - Option value id of the depth.
   * @param {HTMLInputElement} [preferred] - Size radio to select if it belongs to that depth.
   */
  #showDepth(depthId, preferred) {
    const wantedSize = (preferred ?? this.#checkedSize ?? this.#sizeInputs.find((input) => input.checked))?.dataset.size;
    /** @type {HTMLInputElement | undefined} */
    let keep;
    /** @type {HTMLInputElement | undefined} */
    let firstAvailable;
    /** @type {HTMLInputElement | undefined} */
    let first;

    for (const input of this.#sizeInputs) {
      const visible = input.dataset.depth === depthId;
      input.disabled = !visible;
      const label = input.closest('label');
      if (label) label.hidden = !visible;
      if (!visible) continue;

      first ??= input;
      if (!firstAvailable && input.dataset.available === 'true') firstAvailable = input;
      if (!keep && wantedSize && input.dataset.size === wantedSize) keep = input;
    }

    const next = keep ?? firstAvailable ?? first;
    if (next) next.checked = true;
  }

  /** Copies the selected variant's result lines into the live region (no-op when unchanged). */
  #renderResult() {
    const input = this.#checkedSize;
    const region = this.querySelector('[data-zg-sf-result-region]');
    if (!input || !(region instanceof HTMLElement) || region.dataset.variantId === input.value) return;

    const template = this.querySelector(`template[data-zg-sf-result="${CSS.escape(input.value)}"]`);
    if (!(template instanceof HTMLTemplateElement)) return;

    region.replaceChildren(template.content.cloneNode(true));
    region.dataset.variantId = input.value;
  }

  /** Inline: shows the sizes of the checked depth and the checked size's result (no change unless Back restored a choice). */
  #showCheckedDepth() {
    const depthInput = this.#depthInputs.find((input) => input.checked);
    if (!depthInput) return;

    this.#showDepth(depthInput.value);
    this.#renderResult();
  }

  /** Matches the drawer to the page picker's current depth and size. Runs as the drawer opens. */
  #preselectFromPage() {
    const picker = this.#pagePicker();
    const selected = picker ? selectedValueIds(picker) : new Set();
    const depthInputs = this.#depthInputs;

    // A single-value option is printed as text by Horizon (no input), so it can't be read: it's the only depth then.
    const depthInput =
      depthInputs.find((input) => selected.has(input.value)) ??
      (depthInputs.length === 1 ? depthInputs[0] : depthInputs.find((input) => input.checked));
    if (!depthInput) return;

    const sizeInput = this.#sizeInputs.find(
      (input) => input.dataset.depth === depthInput.value && selected.has(input.dataset.size ?? '')
    );

    depthInput.checked = true;
    this.#showDepth(depthInput.value, sizeInput);
    this.#renderResult();
  }

  /** "Shop this size". */
  async #shop() {
    const input = this.#checkedSize;
    if (!input) return;

    const { depth = '', size = '', pack = '', url = '' } = input.dataset;
    const variantId = input.value;
    const picker = this.#pagePicker();

    if (!picker || !(await whenUpgraded(picker, 'updateSelectedOption'))) {
      this.#navigate(url);
      return;
    }

    // Options with a single value have no control in Horizon's picker; they need no selecting. data-pack (the pack
    // option's first value for this size) is only rendered when that option has several values.
    const depthCount = this.#depthInputs.length;
    const sizeCount = new Set(this.#sizeInputs.map((sizeInput) => sizeInput.dataset.size)).size;
    /** @type {[string, boolean][]} Option value id, and whether the picker must have a control for it. */
    const values = [[depth, depthCount > 1]];
    if (pack) values.push([pack, true]);
    // Size last, as without a pack option: when it changes it carries the change event, and the focus below lands on it.
    values.push([size, sizeCount > 1]);
    /** @type {(HTMLInputElement | HTMLOptionElement)[]} */
    const targets = [];

    for (const [valueId, required] of values) {
      const target = picker.querySelector(`[data-option-value-id="${CSS.escape(String(valueId))}"]`);
      if (target instanceof HTMLInputElement || target instanceof HTMLOptionElement) {
        targets.push(target);
      } else if (required) {
        this.#navigate(url);
        return;
      }
    }

    const changed = targets.filter((target) =>
      target instanceof HTMLOptionElement ? !target.selected : !target.checked
    );

    if (changed.length) {
      // Horizon takes the event's variant id (and ?variant=) from the dispatched control's data-variant-id, which the
      // server rendered for the OTHER options' current values. Point it at the real variant; the picker morph that
      // follows the fetch rewrites the attribute from the server anyway.
      const last = /** @type {HTMLInputElement | HTMLOptionElement} */ (changed[changed.length - 1]);
      last.dataset.variantId = variantId;

      const controls = changed.map((target) => {
        if (target instanceof HTMLOptionElement) {
          const select = target.closest('select');
          if (select) {
            select.value = target.value;
            return select;
          }
        }
        return target;
      });

      // Every control but the last is checked through the picker's own API (never the one we dispatch on: calling
      // updateSelectedOption twice on one input corrupts its checked-state bookkeeping), then ONE change event.
      for (const control of controls.slice(0, -1)) picker.updateSelectedOption?.(control);
      controls[controls.length - 1]?.dispatchEvent(new Event('change', { bubbles: true }));
    }

    await this.#drawer?.close?.();
    this.#revealPicker(picker, targets[targets.length - 1]);
  }

  /**
   * After "Shop this size" from a trigger far from the picker (e.g. the install section), bring the picker into view
   * and put focus on the selected size, so the shopper sees and hears what changed.
   * @param {HTMLElement} picker
   * @param {HTMLInputElement | HTMLOptionElement | undefined} target
   */
  #revealPicker(picker, target) {
    const rect = picker.getBoundingClientRect();
    if (rect.bottom > 0 && rect.top < window.innerHeight) return;

    const focusTarget = target instanceof HTMLOptionElement ? target.closest('select') : target;
    focusTarget?.focus({ preventScroll: true });

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    picker.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'instant' : 'smooth' });
  }

  /** @param {string} url */
  #navigate(url) {
    if (url) window.location.assign(url);
  }
}

if (!customElements.get('zg-size-finder')) {
  customElements.define('zg-size-finder', ZgSizeFinder);
}
